import React, { useState, useEffect, useRef } from 'react';
import SideNavbar from './helpers/SideNavbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faImage, faVideo, faLink, faHashtag, faLightbulb, faStar, faPlus, faCamera, faClock, faCheck, faChartBar, faImages, faChevronDown, faUpload, faPoll, faFileText, faSpinner, faExclamationTriangle, faLineChart, faNewspaper, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Modal, Button } from "react-bootstrap";
import TimezoneSelect from 'react-timezone-select';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useFeatureLimits } from './FeatureLimits/FeatureLimitsProvider';
import { FEATURE_NAMES } from '../apis/featureLimits';
import moment from 'moment-timezone';
import { validateProfileComplete } from '../utils/profileValidation';
import { getAllActivePlatforms, formatPlatformNamesForButton, formatPlatformNamesForAlert } from '../utils/platformUtils';
import { validateInstagramImageFile, validateInstagramImageUrl, resizeForInstagram, blobToFile } from '../utils/instagramValidation';

const CreatePostPage = () => {
  const navigate = useNavigate();
  const { logFeatureUsage, checkFeatureAccess } = useFeatureLimits();
  const [postContent, setPostContent] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [hashtags, setHashtags] = useState('');
  const [error, setError] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const fileInputRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [accessToken, setAccessToken] = useState('');
  const [accountype, setAccountType] = useState('');
  const [userId, setUserId] = useState('');
  const [userPrompts, setUserPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aiGeneratedContent, setAiGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [timezone, setTimezone] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [fetchedImages, setFetchedImages] = useState([]);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [isRefreshingImages, setIsRefreshingImages] = useState(false); // Loading state for Refresh button
  const [isCraftingVisual, setIsCraftingVisual] = useState(false); // Loading state for Craft Visual button
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [image, setImage] = useState(null);
  const [selectedImagename, setSelectedImagename] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [imageKeywords, setImageKeywords] = useState(''); // Manual keywords for image search
  const [enabledPlatforms, setEnabledPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]); // Array of platform codes: ['linkedin', 'facebook', 'instagram']
  const [postType, setPostType] = useState('content'); // 'content' or 'poll'
  const [pollData, setPollData] = useState({
    question: '',
    options: ['', '', '', '']
  });
  const [isGeneratingPoll, setIsGeneratingPoll] = useState(false);
  const [showAddPromptModal, setShowAddPromptModal] = useState(false);
  const [newPromptText, setNewPromptText] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  
  // Language selection state
  const [selectedLanguage, setSelectedLanguage] = useState({ value: 'en', label: 'English' });
  
  // Language options for AI content generation
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'zh', label: 'Chinese' },
    { value: 'hi', label: 'Hindi' },
    { value: 'te', label: 'Telugu' },
    { value: 'ar', label: 'Arabic' },
    { value: 'nl', label: 'Dutch' },
    { value: 'pl', label: 'Polish' },
    { value: 'tr', label: 'Turkish' },
    { value: 'sv', label: 'Swedish' },
    { value: 'da', label: 'Danish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'fi', label: 'Finnish' },
    { value: 'cs', label: 'Czech' },
    { value: 'th', label: 'Thai' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'id', label: 'Indonesian' },
    { value: 'ms', label: 'Malay' },
    { value: 'he', label: 'Hebrew' },
    { value: 'uk', label: 'Ukrainian' },
    { value: 'ro', label: 'Romanian' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'el', label: 'Greek' },
    { value: 'bg', label: 'Bulgarian' }
  ];

  // Check if user is on free plan (no active subscription)
  const isFreeUser = !localStorage.getItem('subscriptionEndDate') || localStorage.getItem('subscriptionEndDate') === 'null';

  // Check profile completion on component mount (when user opens Create Post Page/Content Studio)
  useEffect(() => {
    // Check if profile is complete when user opens Create Post Page/Content Studio
    // This prompts the user immediately when they navigate to the page
    if (!validateProfileComplete({ showAlert: true, navigate })) {
      // Profile is incomplete - alert already shown by validateProfileComplete
      // If user clicks "Go to Profile Hub", they will be redirected
    }
  }, [navigate]);

  // Fetch user prompts on component mount
  useEffect(() => {
    fetchUserPrompts();
    
    // Fetch user's default language preference
    const fetchUserLanguage = async () => {
      try {
        const UserID = localStorage.getItem('UserID');
        if (UserID) {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/get-profile-info`, { 
            params: { UserID } 
          });
          
          if (response.data && response.data[0] && response.data[0].Language) {
            const userLanguage = response.data[0].Language;
            const languageOption = languageOptions.find(opt => opt.value === userLanguage);
            if (languageOption) {
              setSelectedLanguage(languageOption);
              console.log('✅ Loaded user language preference:', languageOption);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user language:', error);
        // Continue with default English
      }
    };
    fetchUserLanguage();
  }, []);

  // Fetch platforms based on AccountType
  useEffect(() => {
    const fetchPlatforms = async () => {
      setPlatformsLoading(true);
      const storedAccountType = localStorage.getItem('AccountType');
      
      // For User accounts, automatically use LinkedIn only (no UI needed)
      if (storedAccountType === 'User' || storedAccountType === 'user') {
        setSelectedPlatforms(['linkedin']);
        setEnabledPlatforms([{ name: 'LinkedIn', code: 'linkedin' }]);
        setPlatformsLoading(false);
        return;
      }
      
      // For Page accounts, fetch all active platforms and show selection UI
      const platforms = await getAllActivePlatforms();
      setEnabledPlatforms(platforms);
      // Default to all platforms selected
      if (platforms.length > 0) {
        setSelectedPlatforms(platforms.map(p => p.code));
      }
      setPlatformsLoading(false);
    };
    fetchPlatforms();
  }, []);

  // Set default timezone to user's timezone when scheduleForLater is enabled
  useEffect(() => {
    if (scheduleForLater && !timezone) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezone(userTimezone);
    }
  }, [scheduleForLater, timezone]);

  // Get LinkedIn credentials (same as posttext.jsx)
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('AccessToken');
    const storedUserId = localStorage.getItem('Tokenid');
    const storedAccountType = localStorage.getItem('AccountType');
    if (storedAccessToken && storedUserId && storedAccountType) {
      setAccessToken(storedAccessToken);
      setUserId(storedUserId);
      setAccountType(storedAccountType);
    } else {
      console.error('No accessToken or userId found in localStorage');
    }

  }, [navigate]);

  // Extract keywords from content for better image search
  const extractKeywordsFromContent = (content, originalPrompt) => {
    const keywords = [];
    
    // 1. Always include the original prompt (most relevant)
    if (originalPrompt?.trim()) {
      // Extract key terms from prompt (remove common words, keep nouns/adjectives)
      const promptWords = originalPrompt
        .split(/\s+/)
        .filter(word => word.length > 3 && !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'she', 'use', 'her', 'many', 'than', 'them'].includes(word.toLowerCase()))
        .slice(0, 5); // Take top 5 relevant words
      keywords.push(...promptWords);
    }
    
    // 2. Extract hashtags from content
    const hashtags = content.match(/#[\w]+/g) || [];
    keywords.push(...hashtags.map(tag => tag.replace('#', '')));
    
    // 3. Extract key nouns/phrases from content (first 100 chars usually contain main topic)
    const contentPreview = content.substring(0, 200).toLowerCase();
    // Find capitalized words (likely important nouns)
    const capitalizedWords = content.match(/\b[A-Z][a-z]+\b/g) || [];
    keywords.push(...capitalizedWords.slice(0, 3));
    
    // Remove duplicates and limit to 5-7 keywords
    const uniqueKeywords = [...new Set(keywords)].slice(0, 7);
    return uniqueKeywords.filter(k => k && k.length > 2);
  };

  // Function to fetch images with current keywords
  const fetchImagesWithKeywords = async (isRefresh = false) => {
    if (!aiGeneratedContent?.trim()) {
      return;
    }
    
    // Set appropriate loading state based on which button was clicked
    if (isRefresh) {
      setIsRefreshingImages(true);
    } else if (imageKeywords?.trim()) {
      setIsCraftingVisual(true);
    } else {
        setIsFetchingImages(true);
    }
    
    try {
      // If user has manually entered keywords, use those
      if (imageKeywords?.trim() && !isRefresh) {
        const manualKeywords = imageKeywords.split(',').map(k => k.trim()).filter(k => k);
        await fetchImagesForTags(manualKeywords);
        setIsCraftingVisual(false);
        return;
      }
      
      // Otherwise, use improved automatic extraction
      const extractedKeywords = extractKeywordsFromContent(aiGeneratedContent, customPrompt);
      console.log('Extracted keywords for images:', extractedKeywords);
      
      if (extractedKeywords.length > 0) {
        await fetchImagesForTags(extractedKeywords);
      } else {
        // Fallback: try to generate a title from the content
        try {
          const titleResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-title`, {
            content: aiGeneratedContent,
          });
          const generatedTitle = titleResponse.data.title || '';
          if (generatedTitle) {
            await fetchImagesForTags([generatedTitle]);
          } else {
            setFetchedImages([]);
          }
        } catch (error) {
          console.error('Error generating title:', error);
          setFetchedImages([]);
        }
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setFetchedImages([]);
    } finally {
      setIsFetchingImages(false);
      setIsRefreshingImages(false);
      setIsCraftingVisual(false);
    }
  };

  // Fetch images when AI content is generated - use improved keyword extraction
  // Note: We don't include imageKeywords in deps to avoid re-fetching while user types
  // User can manually refresh with Refresh button
  useEffect(() => {
    if (aiGeneratedContent?.trim()) {
      // Only auto-fetch if no manual keywords set (to avoid conflicts)
      if (!imageKeywords?.trim()) {
        fetchImagesWithKeywords();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiGeneratedContent, customPrompt]);

  const fetchUserPrompts = async () => {
    try {
      const UserID = localStorage.getItem('UserID');
      console.log('Fetching prompts for UserID:', UserID);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getprompts/${UserID}`);
      console.log("API Response:", response.data);
      
      if (!response.data || response.data.length === 0) {
        console.log('No prompts available - setting empty array');
        setUserPrompts([]); // Set empty array instead of returning early
        return;
      }
      
      // Transform the data to match the expected structure
      const transformedPrompts = response.data.map((prompt) => ({
        PromptID: prompt.PromptID,
        PromptData: prompt.PromptData,
        promptName: prompt.PromptData?.substring(0, 50) + '...', // Truncate for display
        promptText: prompt.PromptData
      }));
      
      console.log('Transformed prompts:', transformedPrompts);
      setUserPrompts(transformedPrompts);
    } catch (error) {
      console.error('Error fetching user prompts:', error);
      setUserPrompts([]); // Set empty array on error too
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const generatePromptIdeas = async () => {
    try {
      setIsGenerating(true);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-prompts`, {
        prompt: customPrompt || selectedPrompt,
        UserID: localStorage.getItem('UserID')
      });
      // Handle generated prompt ideas
      console.log('Generated prompt ideas:', response.data);
      // Refresh prompts after generating new ones
      fetchUserPrompts();
    } catch (error) {
      console.error('Error generating prompt ideas:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!newPromptText || !newPromptText.trim()) {
      Swal.fire({ icon: 'warning', title: 'Please enter a prompt' });
      return;
    }
    try {
      setSavingPrompt(true);
      const UserID = localStorage.getItem('UserID');
      const resp = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveprompts`, {
        UserID: Number(UserID),
        PromptData: newPromptText.trim()
      });
      // Refresh prompts after saving
      await fetchUserPrompts();
      setShowAddPromptModal(false);
      setNewPromptText('');
      Swal.fire({ icon: 'success', title: 'Prompt added successfully!' });
    } catch (e) {
      console.error('Failed to save prompt:', e);
      Swal.fire({ icon: 'error', title: 'Failed to save prompt' });
    } finally {
      setSavingPrompt(false);
    }
  };

  const generateContent = async () => {
    try {
      // Block free users from generating polls
      if (postType === 'poll' && isFreeUser) {
        Swal.fire({
          icon: 'warning',
          title: 'Polls Restricted',
          text: 'Polls are restricted for free users. Please upgrade/subscribe to use polls.',
          confirmButtonText: 'OK',
        });
        return;
      }

      // Check limits before crafting content/poll
      // - Polls use POLLS
      // - Scheduled posts use SCHEDULE_DAYS
      // - Manual posts use MANUAL_POSTS
      const featureName = postType === 'poll'
        ? FEATURE_NAMES.POLLS
        : (scheduleForLater ? FEATURE_NAMES.SCHEDULE_DAYS : FEATURE_NAMES.MANUAL_POSTS);
      const accessCheck = await checkFeatureAccess(featureName);
      if (!accessCheck.canProceed) {
        const limitType = postType === 'poll' ? 'Polls' : (scheduleForLater ? 'Scheduled Posts' : 'Create Posts');
        const msg = `You have reached your limit for ${limitType}. ${accessCheck.error || 'Please upgrade to continue.'}`;
        setError(msg);
        Swal.fire({
          icon: 'warning',
          title: 'Limit Exceeded',
          text: msg,
          confirmButtonText: 'OK',
        });
        return;
      }

      const prompt = customPrompt;
      
      if (!prompt.trim()) {
        alert('Please enter a prompt or select from saved prompts');
        return;
      }
      
      if (postType === 'poll') {
        await generatePoll();
      } else {
        await generateRegularContent();
      }
    } catch (error) {
      console.error('Error generating content:', error);
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Error generating content',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const generateRegularContent = async () => {
    try {
      setIsGenerating(true);
      const prompt = customPrompt;
      const UserID = localStorage.getItem('UserID');
      
      // Clear image keywords when generating new content
      setImageKeywords('');
      
      // Use the same API endpoint as pendingpost.js
      const languageToSend = selectedLanguage?.value || 'en';
      console.log('🌐 Sending language to API:', languageToSend, 'Selected language object:', selectedLanguage);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/generateAIContentinUpdatepost`,
        { 
          UserID, 
          Prompts: prompt,
          language: languageToSend // Pass selected language
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
             const newContent = response.data.content;
       
       setAiGeneratedContent(newContent);
    } catch (error) {
      console.error('Error generating content:', error);
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Error generating AI content',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generatePoll = async () => {
    try {
      setIsGeneratingPoll(true);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-poll`, {
        prompt: customPrompt
      });

      if (response.data?.newContent?.question && response.data?.newContent?.options) {
                 setPollData({
           question: response.data.newContent.question,
           options: response.data.newContent.options.slice(0, 4)
         });
         setAiGeneratedContent(''); // Clear regular content when generating poll
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Poll Generation Failed',
          text: 'Failed to generate poll. Please try again.',
          showConfirmButton: true,
        });
      }
    } catch (error) {
      console.error('Error generating poll:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate poll. Please try again.',
        showConfirmButton: true,
      });
    } finally {
      setIsGeneratingPoll(false);
    }
  };

  const validateImage = async (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(null); // Timeout after 3 seconds
      }, 3000);
      
      img.onload = () => {
        clearTimeout(timeout);
        resolve(url);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };
      img.src = url;
    });
  };

  const fetchImagesForTags = async (tags) => {
    if (!tags.length) return;

    try {
      const query = tags.join(" ");
      const response = await axios.get("https://www.googleapis.com/customsearch/v1", {
        params: {
          key: process.env.REACT_APP_GOOGLE_API_KEY,
          cx: process.env.REACT_APP_GOOGLE_CSE_ID,
          q: query,
          searchType: "image",
          num: 10,
          rights: 'cc_publicdomain,cc_attribute',
          safe: 'active',
        },
      });

      if (response.data.items) {
        // Validate only first 8 images in parallel (faster), with timeout
        const itemsToValidate = response.data.items.slice(0, 8);
        const imageLinks = await Promise.all(
          itemsToValidate.map((item) => validateImage(item.link))
        );
        const validImages = imageLinks.filter((link) => link !== null);

        if (validImages.length === 0) {
          setFetchedImages([]);
        } else {
          setFetchedImages(validImages);
        }
      } else {
        setFetchedImages([]);
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      setFetchedImages([]);
    }
  };


  // Display content as-is (same as pendingpost.js)
  const displayContent = aiGeneratedContent;

  const toUnicodeBold = (text) => {
    const boldMap = {
      A: '𝗔', B: '𝗕', C: '𝗖', D: '𝗗', E: '𝗘', F: '𝗙', G: '𝗚', H: '𝗛', I: '𝗜', J: '𝗝', K: '𝗞', L: '𝗟', M: '𝗠', 
      N: '𝗡', O: '𝗢', P: '𝗣', Q: '𝗤', R: '𝗥', S: '𝗦', T: '𝗧', U: '𝗨', V: '𝗩', W: '𝗪', X: '𝗫', Y: '𝗬', Z: '𝗭',
      a: '𝗮', b: '𝗯', c: '𝗰', d: '𝗱', e: '𝗲', f: '𝗳', g: '𝗴', h: '𝗵', i: '𝗶', j: '𝗷', k: '𝗸', l: '𝗹', m: '𝗺',
      n: '𝗻', o: '𝗼', p: '𝗽', q: '𝗾', r: '𝗿', s: '𝘀', t: '𝘁', u: '𝘂', v: '𝘃', w: '𝘄', x: '𝘅', y: '𝘆', z: '𝘇',
      0: '𝟬', 1: '𝟭', 2: '𝟮', 3: '𝟯', 4: '𝟰', 5: '𝟱', 6: '𝟲', 7: '𝟳', 8: '𝟴', 9: '𝟵'
    };
  
    return text.replace(/\*\*(.*?)\*\*/g, (_, match) => {
      return match
        .split(' ')
        .map(word => word.startsWith('#') ? word : word.split('').map(char => boldMap[char] || char).join(''))
        .join(' ');
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    // If no file selected (user cancelled), clear everything
    if (!file) {
      setImage(null);
      setSelectedImagename('');
      setImagePreview(null);
      setVideoPreview(null);
      return;
    }
    
    const maxSize = 50 * 1024 * 1024; // 50 MB in bytes

    if (file.size > maxSize) {
      Swal.fire({
        icon: 'error',
        title: 'File Size Exceeded',
        text: 'The file size should not exceed 50 MB.',
        showConfirmButton: true,
      });
      setImage(null);
      setSelectedImagename('');
      setImagePreview(null);
      setVideoPreview(null);
      return;
    }
    
    // File is valid, proceed with setting it
    setImage(file);
    setSelectedImagename(file.name || '');
    // Clear selected image when uploading file
    setSelectedImageUrl(null);
    
    // Create preview for image files
    if (file.type && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setVideoPreview(null); // Clear video preview when image is selected
      };
      reader.readAsDataURL(file);
    } else if (file.type && file.type.startsWith('video/')) {
      // Create preview for video files
      const reader = new FileReader();
      reader.onload = (e) => {
        setVideoPreview(e.target.result);
        setImagePreview(null); // Clear image preview when video is selected
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
      setVideoPreview(null);
    }
  };

  const handleSelectSuggestedImage = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setSelectedImagename(imageUrl);
    // Clear uploaded file when selecting image
    setImage(null);
    setImagePreview(null);
    setVideoPreview(null);
  };

  const handleDownloadImage = async (imageUrl, e) => {
    e.stopPropagation();
    try {
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from URL or use default
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0] || 'downloaded-image.jpg';
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Failed to download the image. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    }
  };

  const addGeneratedContent = () => {
    setPostContent(aiGeneratedContent);
  };

  const handleSchedulePost = async () => {
    // Validate that at least one platform is selected
    if (!selectedPlatforms || selectedPlatforms.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Platform Selected',
        text: 'Please select at least one platform to schedule to.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192',
        timer: 2000
      });
      return;
    }

    setIsScheduling(true);
    try {
      // Block free users from scheduling polls
      if (postType === 'poll' && isFreeUser) {
        Swal.fire({
          icon: 'warning',
          title: 'Polls Restricted',
          text: 'Polls are restricted for free users. Please upgrade/subscribe to use polls.',
          confirmButtonText: 'OK',
        });
        setIsScheduling(false);
        return;
      }

      if (!accessToken || !userId) {
        throw new Error('Access Token or User ID is missing');
      }

      // No platform settings check - use only selected platforms from UI

      if (!scheduledDate) {
        Swal.fire({
          position: 'center',
          icon: 'warning',
          title: 'Schedule Date Required',
          text: 'Please select a date and time for scheduling.',
          showConfirmButton: true,
        });
        setIsScheduling(false);
        return;
      }

      // Handle both string and object formats for timezone
      const timezoneValue = typeof timezone === 'string' ? timezone : timezone?.value;
      
      // Validate timezone selected
      if (!timezone || !timezoneValue) {
        Swal.fire({
          position: 'center',
          icon: 'warning',
          title: 'Timezone Required',
          text: 'Please select a timezone.',
          showConfirmButton: true,
        });
        setIsScheduling(false);
        return;
      }

      // Validate that the scheduled date is not in the past
      // Parse the datetime-local string (format: "YYYY-MM-DDTHH:mm")
      const dateStr = scheduledDate; // e.g., "2025-11-04T12:18"
      const [datePart, timePart] = dateStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = (timePart || '').split(':').map(Number);
      
      // Get current date/time in the selected timezone for comparison
      const now = new Date();
      const nowInTz = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneValue,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(now);
      
      const nowYear = parseInt(nowInTz.find(p => p.type === 'year').value);
      const nowMonth = parseInt(nowInTz.find(p => p.type === 'month').value);
      const nowDay = parseInt(nowInTz.find(p => p.type === 'day').value);
      const nowHour = parseInt(nowInTz.find(p => p.type === 'hour').value);
      const nowMinute = parseInt(nowInTz.find(p => p.type === 'minute').value);
      
      // Compare scheduled date/time with current date/time in the selected timezone
      const scheduledDateNum = year * 10000 + month * 100 + day;
      const nowDateNum = nowYear * 10000 + nowMonth * 100 + nowDay;
      const scheduledTimeNum = hour * 100 + minute;
      const nowTimeNum = nowHour * 100 + nowMinute;
      
      // Check if scheduled time is in the past
      if (scheduledDateNum < nowDateNum || 
          (scheduledDateNum === nowDateNum && scheduledTimeNum <= nowTimeNum)) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Invalid Schedule Time',
          text: 'Please select a date and time in the future. The scheduled time cannot be in the past.',
          showConfirmButton: true,
        });
        setIsScheduling(false);
        return;
      }
      
      // Create scheduledDateTime for use later in the function
      const scheduledDateTime = new Date(year, month - 1, day, hour, minute);

      // Check feature access for scheduled posts
      const featureName = postType === 'poll' ? FEATURE_NAMES.POLLS : FEATURE_NAMES.SCHEDULE_DAYS;
      const accessCheck = await checkFeatureAccess(featureName);
      if (!accessCheck.canProceed) {
        const limitType = postType === 'poll' ? 'Polls' : 'Scheduled Posts';
        const msg = `You have reached your limit for ${limitType}. ${accessCheck.error || 'Please upgrade to continue.'}`;
        setError(msg);
        Swal.fire({
          icon: 'warning',
          title: 'Limit Exceeded',
          text: msg,
          confirmButtonText: 'OK',
        });
        setIsScheduling(false);
        return;
      }

      // Apply bold formatting to content
      let contentToUse = aiGeneratedContent || postContent;
      
      // For poll posts, don't add poll content to main text - let LinkedIn handle the poll display
      // The poll data will be sent separately in the API call
      
      const plainTextContent = contentToUse
        ? toUnicodeBold(contentToUse)
            .replace(/<\/?br\s*\/?>/g, '\n')  
            .replace(/<\/?p>/g, '\n')        
            .replace(/<\/?[^>]+(>|$)/g, '')  
        : toUnicodeBold(postContent);

      let imageAsset = null;
      let videoAsset = null;
      let videoUrlToSave = null; // Video URL for database saving
      let uploadedImageUrl = null; // URL for database saving (image or video)

      // Handle uploaded file
      if (image) {
        console.log('Debug - Processing uploaded image:', image.name, image.type);
        const formData = new FormData();
        formData.append('file', image);
        formData.append('userId', userId);

        if (image.type.startsWith('image/')) {
          console.log('Debug - Uploading image to blob storage...');
          try {
          // Use blob storage upload instead of LinkedIn upload
          const imageUploadResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          imageAsset = imageUploadResponse.data.imageUrl;
          uploadedImageUrl = imageAsset; // Set uploadedImageUrl for scheduled posts
          console.log('Blob upload successful, imageAsset:', imageAsset);
          } catch (error) {
            console.error('Error uploading image to blob storage:', error);
            await Swal.fire({
              icon: 'error',
              title: 'Image Upload Failed',
              html: `
                <div style="text-align: left;">
                  <p><strong>Image upload failed. Please try the following:</strong></p>
                  <ol style="margin-left: 20px; padding-left: 10px;">
                    <li>Download the image to your local Downloads folder</li>
                    <li>Remove the image from the media section</li>
                    <li>Re-upload the image from your local Downloads folder</li>
                  </ol>
                </div>
              `,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192'
            });
            setIsPublishing(false);
            return;
          }
        } else if (image.type.startsWith('video/')) {
          try {
            // ✅ Upload video to blob storage first to get URL for database saving (like KnackMoments)
            console.log('📤 Uploading video to blob storage for DB save...');
            const blobFormData = new FormData();
            blobFormData.append('file', image);
            blobFormData.append('userId', userId);
            
            const blobUploadResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
              blobFormData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            
            // Get video URL for database saving
            videoUrlToSave = blobUploadResponse.data.imageUrl || blobUploadResponse.data.url || null;
            uploadedImageUrl = videoUrlToSave; // Set uploadedImageUrl for scheduled posts
            console.log('✅ Video blob upload successful, videoUrl for DB:', videoUrlToSave);
            
            // Note: No LinkedIn upload needed for scheduled posts - it will happen when post is published
          } catch (error) {
            console.error('Error uploading video to blob storage:', error);
            await Swal.fire({
              icon: 'error',
              title: 'Video Upload Failed',
              html: `
                <div style="text-align: left;">
                  <p><strong>Video upload failed. Please try the following:</strong></p>
                  <ol style="margin-left: 20px; padding-left: 10px;">
                    <li>Download the video to your local Downloads folder</li>
                    <li>Remove the video from the media section</li>
                    <li>Re-upload the video from your local Downloads folder</li>
                  </ol>
                </div>
              `,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192'
            });
            setIsPublishing(false);
            return;
          }
        }
      }

      // Handle selected image from AI suggestions
      if (selectedImageUrl && !image) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/download-image?url=${encodeURIComponent(selectedImageUrl)}`);
          if (!response.ok) {
            throw new Error('Failed to download image');
          }
          const blob = await response.blob();
          const fileName = `image_${Date.now()}.jpg`;
          const file = new File([blob], fileName, { type: blob.type });

          const formData = new FormData();
          formData.append('file', file);
          formData.append('userId', userId);

          // For scheduled posts, use blob storage upload
          const imageUploadResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          imageAsset = imageUploadResponse.data.imageUrl;
          uploadedImageUrl = imageAsset; // Set uploadedImageUrl for scheduled posts
          console.log('Blob upload successful, imageAsset:', imageAsset);
        } catch (error) {
          console.error("Error downloading or uploading image:", error);
          await Swal.fire({
            icon: 'error',
            title: 'Image Upload Failed',
            html: `
              <div style="text-align: left;">
                <p><strong>Image upload failed. Please try the following:</strong></p>
                <ol style="margin-left: 20px; padding-left: 10px;">
                  <li>Download the image to your local Downloads folder</li>
                  <li>Remove the image from the media section</li>
                  <li>Re-upload the image from your local Downloads folder</li>
                </ol>
              </div>
            `,
            confirmButtonText: 'OK',
            confirmButtonColor: '#2e3192'
          });
          setIsPublishing(false);
          return;
        }
      }

      // Create a new API endpoint that handles both tables simultaneously
      const UserID = localStorage.getItem('UserID');
      const RoleID = localStorage.getItem('RoleID');
      const SelectedUserAccountId = localStorage.getItem('SelectedUserAccountId');
      const userProfile = JSON.parse(localStorage.getItem("userProfile")) || [];
      const AccountId = userProfile ? userProfile[0]?.AccountId : null;
      
      console.log('Debug - UserID:', UserID);
      console.log('Debug - RoleID:', RoleID);
      console.log('Debug - AccountId:', AccountId);
      console.log('Debug - SelectedUserAccountId:', SelectedUserAccountId);
      console.log('Debug - userId (Tokenid):', userId);
      console.log('Debug - accessToken:', accessToken);
      console.log('Debug - accountype:', accountype);
      
      // Prepare poll data if it's a poll post
      let pollQuestionWithOptions = null;
      if (postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim())) {
        pollQuestionWithOptions = {
          question: pollData.question,
          options: pollData.options.filter(opt => opt.trim())
        };
      }

      console.log('Debug - imageAsset:', imageAsset);
      console.log('Debug - selectedImageUrl:', selectedImageUrl);
      console.log('Debug - image:', image);

      // Use simple date parsing like before
      console.log('Debug - scheduledDate string:', scheduledDate);
      console.log('Debug - scheduledDateTime:', scheduledDateTime);
      
      // Ensure consistent date parsing by using local date methods
      // Note: year, month, day are already parsed above from the dateStr
      const startDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      console.log('Debug - Date components:', { year, month, day, startDate });
      console.log('Debug - Day of week:', scheduledDateTime.toLocaleDateString('en-US', { weekday: 'long' }));
      
      // Use selected platforms directly
      const platformsToSchedule = selectedPlatforms;

      // Validate that at least one platform is selected
      if (!platformsToSchedule || platformsToSchedule.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Platform Selected',
          text: 'Please select at least one platform to schedule the post to.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
        });
        setIsScheduling(false);
        return;
      }

      const scheduleData = {
        Days: scheduledDateTime.toLocaleDateString('en-US', { weekday: 'long' }), // Get day name
        ScheduleTime: scheduledDateTime.toLocaleTimeString('en-US', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit' 
        }), // HH:mm format
        StartDate: startDate, // YYYY-MM-DD format
        EndDate: startDate, // Same as StartDate for single post
        // Save the actual prompt text when user enters it; otherwise use selected prompt id/name
        Prompt: (customPrompt && customPrompt.trim()) ? customPrompt.trim() : (selectedPrompt || 'custom'),
        AccessToken: RoleID == 1 ? (userProfile[0]?.AccessToken || accessToken) : accessToken,
        Tokenid: RoleID == 1 ? (userProfile[0]?.Tokenid || userId) : userId,
        TimeZone: timezoneValue, // Use timezoneValue to handle both string and object formats
        Email: '', // No email for manual posts
        AccountId: RoleID == 1 || RoleID == 4 ? AccountId : SelectedUserAccountId,
        UserID: UserID,
        Status: 'APPROVED',
        PromptType: 1,
        AccountType: accountype,
        Content: plainTextContent, // Send the generated content
        Image: (image && image.type && image.type.startsWith('video/')) 
          ? (uploadedImageUrl || 'uploaded') 
          : (imageAsset || uploadedImageUrl || selectedImageUrl || (image ? 'uploaded' : null)), // Send image/video info
        Video: (image && image.type && image.type.startsWith('video/')) 
          ? (uploadedImageUrl || 'uploaded') 
          : null, // Send video URL if it's a video
        PostType: postType, // 'content' or 'poll'
        PollQuestionWithOptions: pollQuestionWithOptions ? JSON.stringify(pollQuestionWithOptions) : null,
        PollRequired: postType === 'poll' ? true : false,
        customPrompt: customPrompt, // Send the custom prompt text
        platforms: platformsToSchedule // Send selected platforms array (with LinkedIn default for User accounts)
      };

      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/create-post-schedule`,
        scheduleData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (resp.status === 200) {
        // Log feature usage based on post type
        try {
          const featureName = postType === 'poll' ? FEATURE_NAMES.POLLS : FEATURE_NAMES.SCHEDULE_DAYS;
          await logFeatureUsage(featureName, 1, JSON.stringify({
            scheduleType: 'post_schedule',
            scheduledDate: scheduledDate,
            postType: postType,
            hasImage: !!(image || selectedImageUrl),
            hasPoll: postType === 'poll' && pollData.question
          }));
        } catch (usageError) {
          console.error('Error logging feature usage:', usageError);
        }

        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Post Scheduled Successfully',
          text: `Your post has been scheduled for ${new Date(scheduledDate).toLocaleString()}`,
          showConfirmButton: false,
          timer: 3000,
        });

        // Reset form
        setPostContent('');
        setAiGeneratedContent('');
        setCustomPrompt('');
        setSelectedImages([]);
        setHashtags('');
        setImage(null);
        setSelectedImagename('');
        setSelectedImageUrl(null);
        setFetchedImages([]);
        setImagePreview(null);
        setVideoPreview(null);
        setScheduledDate('');
        setScheduleForLater(false);
        setPollData({ question: '', options: ['', '', '', ''] });
        setPostType('content');

        // Refresh feature usage data
        if (window.refreshFeatureUsage) {
          window.refreshFeatureUsage();
        }
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Error scheduling post!',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Error scheduling post:', error.response ? error.response.data : error.message);
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Failed to schedule post.',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handlePublish = async () => {
    // Validate that at least one platform is selected
    if (!selectedPlatforms || selectedPlatforms.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Platform Selected',
        text: 'Please select at least one platform to post to.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192',
        timer: 2000
      });
      return;
    }

    setIsPublishing(true);
    try {
      // Block free users from posting polls
      if (postType === 'poll' && isFreeUser) {
        Swal.fire({
          icon: 'warning',
          title: 'Polls Restricted',
          text: 'Polls are restricted for free users. Please upgrade/subscribe to use polls.',
          confirmButtonText: 'OK',
        });
        setIsPublishing(false);
        return;
      }

      if (!accessToken || !userId) {
        throw new Error('Access Token or User ID is missing');
      }

      // Check if Instagram is selected and require image
      const isInstagramEnabled = selectedPlatforms.includes('instagram');
      const hasImage = !!(image || selectedImageUrl);
      
      if (isInstagramEnabled && !hasImage && postType !== 'poll') {
        Swal.fire({
          icon: 'error',
          title: 'Image Required for Instagram',
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 15px;"><strong>Instagram requires an image to post.</strong></p>
              <p style="margin-bottom: 20px; color: #666;">Please add an image to your post before publishing to Instagram.</p>
            </div>
          `,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
        });
        setIsPublishing(false);
        return;
      }

      // Validate Instagram image aspect ratio if Instagram is enabled and image exists
      if (isInstagramEnabled && hasImage && postType !== 'poll') {
        try {
          let validationResult;
          
          if (image) {
            // Validate uploaded file
            validationResult = await validateInstagramImageFile(image);
          } else if (selectedImageUrl) {
            // Validate selected image URL
            validationResult = await validateInstagramImageUrl(selectedImageUrl);
          }
          
          if (validationResult && !validationResult.isValid) {
            await Swal.fire({
              icon: 'error',
              title: 'Invalid Image Aspect Ratio for Instagram',
              html: `
                <div style="text-align: left;">
                  <p style="margin-bottom: 15px;"><strong>${validationResult.error || 'Image does not meet Instagram requirements.'}</strong></p>
                  <p style="margin-bottom: 20px; color: #666;">Instagram requires images with aspect ratios between 0.8:1 and 1.91:1 (approximately 4:5 portrait to 1.91:1 landscape).</p>
                  <p style="color: #666;">Please select a different image with a valid aspect ratio.</p>
                </div>
              `,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192',
            });
            setIsPublishing(false);
            return;
          }
        } catch (validationError) {
          console.error('Error validating Instagram image:', validationError);
          // If validation fails due to technical issues, warn but allow to proceed
          // (backend will catch it anyway)
          const result = await Swal.fire({
            icon: 'warning',
            title: 'Image Validation Warning',
            text: 'Unable to validate image aspect ratio. The post will be attempted, but Instagram may reject it if the aspect ratio is invalid.',
            confirmButtonText: 'Continue',
            confirmButtonColor: '#2e3192',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            cancelButtonColor: '#6c757d',
          });
          
          if (!result.isConfirmed) {
            setIsPublishing(false);
            return;
          }
        }
      }

      // No platform settings check - use only selected platforms from UI

      // Check feature access based on post type
      const featureName = postType === 'poll' ? FEATURE_NAMES.POLLS : FEATURE_NAMES.MANUAL_POSTS;
      const accessCheck = await checkFeatureAccess(featureName);
      if (!accessCheck.canProceed) {
        const limitType = postType === 'poll' ? 'Polls' : 'Create posts';
        const msg = `You have reached your limit for ${limitType}. ${accessCheck.error || 'Please upgrade to continue.'}`;
        setError(msg);
        Swal.fire({
          icon: 'warning',
          title: 'Limit Exceeded',
          text: msg,
          confirmButtonText: 'OK',
        });
        setIsPublishing(false);
        return;
      }


      // Apply bold formatting to content
      let contentToUse = aiGeneratedContent || postContent;
      
      // For poll posts, don't add poll content to main text - let LinkedIn handle the poll display
      // The poll data will be sent separately in the API call
      
      const plainTextContent = contentToUse
        ? toUnicodeBold(contentToUse)
            .replace(/<\/?br\s*\/?>/g, '\n')  
            .replace(/<\/?p>/g, '\n')        
            .replace(/<\/?[^>]+(>|$)/g, '')  
        : toUnicodeBold(postContent);

      let imageAsset = null;
      let videoAsset = null;
      let videoUrlToSave = null; // Video URL for database saving
      
      // Handle uploaded file
      let uploadedImageUrl = null;
      if (image) {
        console.log('Debug - Processing uploaded image:', image.name, image.type);
        const formData = new FormData();
        formData.append('file', image);
        formData.append('accessToken', accessToken);
        formData.append('userId', userId);
        formData.append('accountype', accountype);
      
        if (image.type.startsWith('image/')) {
          // ✅ Check if Instagram is enabled and resize image to 1080x1080 (1:1) for Instagram
          const isInstagramEnabled = selectedPlatforms.includes('instagram') || selectedPlatforms.includes('Instagram');
          let imageToUpload = image; // Default to original image
          
          if (isInstagramEnabled) {
            try {
              console.log('📐 Resizing image to 1080x1080 (1:1) for Instagram...');
              const resizedBlob = await resizeForInstagram(image);
              const resizedFile = blobToFile(resizedBlob, `instagram-${Date.now()}.jpg`);
              imageToUpload = resizedFile;
              console.log('✅ Image resized successfully for Instagram');
              
              // Update formData with resized image for LinkedIn upload
              formData.delete('file');
              formData.append('file', resizedFile);
            } catch (resizeError) {
              console.error('❌ Error resizing image for Instagram:', resizeError);
              // Continue with original image if resize fails
              await Swal.fire({
                icon: 'warning',
                title: 'Image Resize Warning',
                text: 'Failed to resize image for Instagram. The original image will be used, but Instagram may reject it if the aspect ratio is invalid.',
                showConfirmButton: true,
                confirmButtonText: 'Continue',
                confirmButtonColor: '#2e3192',
                timer: 5000
              });
            }
          }
          
          // ✅ Upload to blob storage first to get URL for saving to database
          // This MUST succeed for the image to be saved to DB
          try {
            const blobFormData = new FormData();
            blobFormData.append('file', imageToUpload); // Use resized image if Instagram is enabled
            blobFormData.append('userId', userId);
            console.log('📤 Uploading image to blob storage for DB save...');
            const blobUploadResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
              blobFormData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            
            // Handle different response formats
            if (blobUploadResponse.data) {
              // Try different possible response formats
              uploadedImageUrl = blobUploadResponse.data.url || 
                                blobUploadResponse.data.imageUrl || 
                                blobUploadResponse.data.ImageUrl ||
                                blobUploadResponse.data.URL ||
                                blobUploadResponse.data;
              
              // If it's still not a string, try to extract it
              if (typeof uploadedImageUrl !== 'string') {
                console.error('❌ Blob upload response format unexpected:', blobUploadResponse.data);
                throw new Error('Blob upload response format unexpected');
              }
              
              console.log('✅ Blob upload successful, imageUrl for DB:', uploadedImageUrl);
            } else {
              console.error('❌ Blob upload response missing data:', blobUploadResponse);
              throw new Error('Blob upload response missing data');
            }
          } catch (error) {
            console.error('❌ Error uploading to blob storage:', error);
            console.error('❌ Blob upload error details:', {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status
            });
            
            // If blob upload fails, show warning but continue
            // The image won't be saved to DB, but post will still work
            await Swal.fire({
              icon: 'warning',
              title: 'Image Storage Warning',
              text: 'Image uploaded to LinkedIn but may not be saved to database. The post will still be published.',
              showConfirmButton: true,
              confirmButtonText: 'Continue',
              confirmButtonColor: '#2e3192',
              timer: 5000
            });
            
            // Don't set uploadedImageUrl - it will be null, so image won't be saved to DB
            // But we continue with LinkedIn posting
          }
          
          console.log('Debug - Uploading image to LinkedIn for immediate post...');
          try {
          // For immediate posts, use LinkedIn upload to get URN
          const imageUploadResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/upload-image`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          imageAsset = imageUploadResponse.data.asset;
          console.log('LinkedIn upload successful, imageAsset:', imageAsset);
          } catch (error) {
            console.error('Error uploading image to LinkedIn:', error);
            await Swal.fire({
              icon: 'error',
              title: 'Image Upload Failed',
              html: `
                <div style="text-align: left;">
                  <p><strong>Image upload failed. Please try the following:</strong></p>
                  <ol style="margin-left: 20px; padding-left: 10px;">
                    <li>Download the image to your local Downloads folder</li>
                    <li>Remove the image from the media section</li>
                    <li>Re-upload the image from your local Downloads folder</li>
                  </ol>
                </div>
              `,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192'
            });
            setIsPublishing(false);
            return;
          }
        } else if (image.type.startsWith('video/')) {
          try {
            // ✅ Upload video to blob storage first to get URL for database saving (like KnackMoments)
            console.log('📤 Uploading video to blob storage for DB save...');
            const blobFormData = new FormData();
            blobFormData.append('file', image);
            blobFormData.append('userId', userId);
            
            const blobUploadResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
              blobFormData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            
            // Get video URL for database saving
            videoUrlToSave = blobUploadResponse.data.imageUrl || blobUploadResponse.data.url || null;
            uploadedImageUrl = videoUrlToSave; // Set uploadedImageUrl for immediate posts
            console.log('✅ Video blob upload successful, videoUrl for DB:', videoUrlToSave);
            
            // Also upload to LinkedIn for immediate posting
            console.log('📤 Uploading video to LinkedIn for immediate post...');
            const videoUploadResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/upload-video`,
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            videoAsset = videoUploadResponse.data.asset;
            console.log('✅ Video LinkedIn upload successful, videoAsset:', videoAsset);
          } catch (error) {
            console.error('Error uploading video:', error);
            await Swal.fire({
              icon: 'error',
              title: 'Video Upload Failed',
              html: `
                <div style="text-align: left;">
                  <p><strong>Video upload failed. Please try the following:</strong></p>
                  <ol style="margin-left: 20px; padding-left: 10px;">
                    <li>Download the video to your local Downloads folder</li>
                    <li>Remove the video from the media section</li>
                    <li>Re-upload the video from your local Downloads folder</li>
                  </ol>
                </div>
              `,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192'
            });
            setIsPublishing(false);
            return;
          }
        }
      }
      
      // Handle selected image from AI suggestions
      if (selectedImageUrl && !image) {
        try {
          // ✅ Check if Instagram is enabled and resize image FIRST before any uploads
          const isInstagramEnabled = selectedPlatforms.includes('instagram') || selectedPlatforms.includes('Instagram');
          
          // Download the image first
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/download-image?url=${encodeURIComponent(selectedImageUrl)}`);
          if (!response.ok) {
            throw new Error('Failed to download image');
          }
          const blob = await response.blob();
          const fileName = `image_${Date.now()}.jpg`;
          let file = new File([blob], fileName, { type: blob.type });

          // ✅ ALWAYS resize image to 1080x1080 (1:1) for Instagram BEFORE uploading
          // This fixes invalid aspect ratios and ensures Instagram compatibility
          if (isInstagramEnabled) {
            try {
              console.log('📐 Resizing AI-suggested image to 1080x1080 (1:1) for Instagram...');
              const resizedBlob = await resizeForInstagram(selectedImageUrl);
              file = blobToFile(resizedBlob, `instagram-${Date.now()}.jpg`);
              console.log('✅ AI-suggested image resized successfully for Instagram (1080x1080)');
            } catch (resizeError) {
              console.error('❌ Error resizing AI-suggested image for Instagram:', resizeError);
              // Try one more time with the downloaded blob directly
              try {
                console.log('🔄 Retrying resize with downloaded blob...');
                const resizedBlob = await resizeForInstagram(file);
                file = blobToFile(resizedBlob, `instagram-${Date.now()}.jpg`);
                console.log('✅ Image resized successfully on retry');
              } catch (retryError) {
                console.error('❌ Retry also failed:', retryError);
                // Show error and stop posting if resize fails completely
                await Swal.fire({
                  icon: 'error',
                  title: 'Image Resize Failed',
                  html: `
                    <div style="text-align: left;">
                      <p style="margin-bottom: 10px;"><strong>Failed to resize image for Instagram:</strong></p>
                      <p style="margin-bottom: 10px; color: #666;">${resizeError.message || 'The image may be corrupted or invalid.'}</p>
                      <p style="margin-bottom: 10px; color: #666;">Please try:</p>
                      <ol style="margin-left: 20px; padding-left: 10px; color: #666;">
                        <li>Selecting a different AI-suggested image</li>
                        <li>Or uploading an image from your device</li>
                      </ol>
                    </div>
                  `,
                  showConfirmButton: true,
                  confirmButtonText: 'OK',
                  confirmButtonColor: '#2e3192'
                });
                setIsPublishing(false);
                return;
              }
            }
          }

          // ✅ Upload resized image to blob storage FIRST to get URL for database saving
          // This ensures the backend validates the resized image, not the original
          try {
            const blobFormData = new FormData();
            blobFormData.append('file', file); // Use resized file if Instagram is enabled
            blobFormData.append('userId', userId);
            console.log('📤 Uploading resized image to blob storage for DB save...');
            const blobUploadResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/upload-image-blob`,
              blobFormData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              }
            );
            
            // Get the uploaded image URL for database saving
            uploadedImageUrl = blobUploadResponse.data.url || 
                              blobUploadResponse.data.imageUrl || 
                              blobUploadResponse.data.ImageUrl ||
                              blobUploadResponse.data.URL ||
                              blobUploadResponse.data;
            
            if (typeof uploadedImageUrl !== 'string') {
              console.error('❌ Blob upload response format unexpected:', blobUploadResponse.data);
              throw new Error('Blob upload response format unexpected');
            }
            
            console.log('✅ Resized image uploaded to blob storage, URL for DB:', uploadedImageUrl);
          } catch (blobError) {
            console.error('❌ Error uploading resized image to blob storage:', blobError);
            // Fallback to original URL if blob upload fails
            uploadedImageUrl = selectedImageUrl;
            console.warn('⚠️ Using original AI-suggested URL as fallback:', uploadedImageUrl);
          }

          // Validate the file before uploading
          if (!file || file.size === 0) {
            throw new Error('Invalid image file. The resized image is empty or corrupted.');
          }
          
          console.log('📤 File ready for upload:', {
            name: file.name,
            size: file.size,
            type: file.type
          });

          const formData = new FormData();
          formData.append('file', file); // Use resized file for LinkedIn upload too
          formData.append('accessToken', accessToken);
          formData.append('userId', userId);
          formData.append('accountype', accountype);

          // For immediate posts, use LinkedIn upload to get URN
          console.log('📤 Uploading image to LinkedIn...');
          const imageUploadResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/upload-image`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          imageAsset = imageUploadResponse.data.asset;
          console.log('✅ LinkedIn upload successful, imageAsset:', imageAsset);
        } catch (error) {
          console.error("❌ Error in AI-suggested image processing:", error);
          console.error("❌ Error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            stack: error.stack
          });
          
          // Provide more specific error message based on what failed
          let errorMessage = 'Image upload failed.';
          let errorDetails = '';
          
          if (error.message?.includes('resize')) {
            errorMessage = 'Failed to resize image for Instagram.';
            errorDetails = 'The image could not be resized to the required format.';
          } else if (error.message?.includes('download')) {
            errorMessage = 'Failed to download image.';
            errorDetails = 'Could not download the AI-suggested image from the server.';
          } else if (error.message?.includes('blob')) {
            errorMessage = 'Failed to upload image to storage.';
            errorDetails = 'Could not save the image to cloud storage.';
          } else if (error.response?.status === 413) {
            errorMessage = 'Image file too large.';
            errorDetails = 'The image file exceeds the maximum size limit.';
          } else if (error.response?.status === 400) {
            errorMessage = 'Invalid image format.';
            errorDetails = 'The image format is not supported or the file is corrupted.';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
            errorDetails = error.message || 'Please try again or use a different image.';
          } else {
            errorDetails = error.message || 'An unexpected error occurred.';
          }
          
          await Swal.fire({
            icon: 'error',
            title: 'Image Upload Failed',
            html: `
              <div style="text-align: left;">
                <p style="margin-bottom: 10px;"><strong>${errorMessage}</strong></p>
                ${errorDetails ? `<p style="margin-bottom: 15px; color: #666;">${errorDetails}</p>` : ''}
                <p style="margin-bottom: 10px; color: #666;"><strong>Please try the following:</strong></p>
                <ol style="margin-left: 20px; padding-left: 10px; color: #666;">
                  <li>Select a different AI-suggested image</li>
                  <li>Or download the image and upload it from your device</li>
                  <li>Or try again in a few moments</li>
                </ol>
              </div>
            `,
            confirmButtonText: 'OK',
            confirmButtonColor: '#2e3192',
            width: '500px'
          });
          setIsPublishing(false);
          return;
        }
      }

      // Prepare poll data if it's a poll post
      let pollQuestionWithOptions = null;
      if (postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim())) {
        pollQuestionWithOptions = {
          question: pollData.question,
          options: pollData.options.filter(opt => opt.trim())
        };
      }

      // ✅ Prepare image URL for database - prioritize uploaded blob URL, then AI-suggested URL
      // For videos, use videoUrlToSave; for images, use uploadedImageUrl or selectedImageUrl
      // This ensures the image/video URL is saved to the database for immediate posts
      const imageUrlToSave = (image && image.type && image.type.startsWith('video/')) 
        ? (videoUrlToSave || null)
        : (uploadedImageUrl || selectedImageUrl || null);
      console.log('📋 Final image URL to save to DB:', imageUrlToSave, {
        uploadedImageUrl,
        selectedImageUrl,
        hasImage: !!image,
        hasSelectedImageUrl: !!selectedImageUrl,
        imageUrlToSave: imageUrlToSave
      });
      
      // Warn if no image URL is available for database saving
      if (image && !imageUrlToSave) {
        console.warn('⚠️ WARNING: Image file uploaded but no URL available for database saving!');
      }

      // Calculate ScheduledTimeAsPerZone in the user's timezone
      // Get user's timezone - use selected timezone if available, otherwise use browser's timezone
      // Handle both string and object formats for timezone
      const userTimezone = (typeof timezone === 'string' ? timezone : timezone?.value) || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const nowInUserTimezone = moment.tz(userTimezone);
      const scheduledTimeAsPerZone = nowInUserTimezone.format('HH:mm:ss');

      // Use selected platforms directly
      const platformsToPublish = selectedPlatforms;

      // Validate that at least one platform is selected
      if (!platformsToPublish || platformsToPublish.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Platform Selected',
          text: 'Please select at least one platform to post to.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
        });
        setIsPublishing(false);
        return;
      }

      // Post the content with formatting intact
      const postPayload = {
        accessToken,
        userId,
        text: plainTextContent,
        imageAsset,
        videoAsset,
        accountype,
        postType: postType,
        pollQuestionWithOptions: pollQuestionWithOptions ? JSON.stringify(pollQuestionWithOptions) : null,
        pollRequired: postType === 'poll' ? true : false,
        prompt: customPrompt || null, // Send the prompt if present
        imageUrl: imageUrlToSave, // ✅ Send image URL for database saving
        timezone: userTimezone, // Send timezone
        scheduledTimeAsPerZone: scheduledTimeAsPerZone, // Send scheduled time in user's timezone
        platforms: platformsToPublish // Send selected platforms array (with LinkedIn default for User accounts)
      };
      
      console.log('📤 Sending post request with payload:', {
        ...postPayload,
        imageUrl: imageUrlToSave, // Log imageUrl separately for visibility
        hasImageUrl: !!imageUrlToSave
      });
      
      const resp = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/post-content`,
        postPayload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Check for image validation errors BEFORE processing success
      if (resp.data?.error && resp.data?.errorType === 'image_validation') {
        const platformName = resp.data.platform === 'instagram' ? 'Instagram' : resp.data.platform;
        const errorDetails = resp.data.errorDetails || {};
        const aspectRatio = errorDetails.aspectRatio || 'N/A';
        const dimensions = errorDetails.dimensions || 'N/A';
        
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Error`,
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 10px;"><strong>${resp.data.message}</strong></p>
              ${dimensions !== 'N/A' ? `<p style="margin-bottom: 5px;">Image dimensions: ${dimensions}</p>` : ''}
              ${aspectRatio !== 'N/A' ? `<p style="margin-bottom: 5px;">Aspect ratio: ${aspectRatio}:1</p>` : ''}
              <p style="margin-top: 10px; color: #666;">Please upload a different image with a valid aspect ratio (between 0.8:1 and 1.91:1).</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          width: '500px'
        });
        return; // Don't proceed with success handling
      }
      
      // Check for image required errors
      if (resp.data?.error && resp.data?.errorType === 'image_required') {
        const platformName = resp.data.platform === 'instagram' ? 'Instagram' : resp.data.platform;
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Required`,
          text: resp.data.message || 'Image is required for this platform.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
        return; // Don't proceed with success handling
      }

      if (resp.status === 200) {
        // Check if Instagram failed when it was enabled
        const isInstagramEnabled = enabledPlatforms.some(p => p.code === 'instagram' || p.name?.toLowerCase() === 'instagram');
        const instagramPost = resp.data?.instagramPost;
        const facebookPost = resp.data?.facebookPost;
        const linkedinPostId = resp.data?.linkedinPostId;
        
        // Check if platforms were enabled and failed
        const isFacebookEnabled = enabledPlatforms.some(p => p.code === 'facebook' || p.name?.toLowerCase() === 'facebook');
        const onlyInstagramEnabled = isInstagramEnabled && selectedPlatforms.length === 1;
        const instagramFailed = instagramPost && !instagramPost.success;
        const facebookFailed = facebookPost && !facebookPost.success;
        
        // Check if all enabled platforms failed
        const allFailed = 
          (isInstagramEnabled && instagramFailed && !facebookPost?.success && !linkedinPostId) ||
          (onlyInstagramEnabled && instagramFailed) ||
          (isFacebookEnabled && facebookFailed && !instagramPost?.success && !linkedinPostId);
        
        // Only log usage and show success if at least one platform succeeded
        if (!allFailed && resp.data?.success !== false) {
          // Log feature usage based on post type
          try {
            const featureName = postType === 'poll' ? FEATURE_NAMES.POLLS : FEATURE_NAMES.MANUAL_POSTS;
            await logFeatureUsage(featureName, 1, JSON.stringify({
              postType: postType,
              hasImage: !!(image || selectedImageUrl),
              hasPoll: postType === 'poll' && pollData.question
            }));
          } catch (usageError) {
            console.error('Error logging feature usage:', usageError);
          }

          // Build success message based on which platforms succeeded
          const succeededPlatforms = [];
          if (linkedinPostId) succeededPlatforms.push('LinkedIn');
          if (facebookPost?.success) succeededPlatforms.push('Facebook');
          if (instagramPost?.success) succeededPlatforms.push('Instagram');
          
          let successMessage = 'Posted successfully';
          if (succeededPlatforms.length > 0) {
            if (succeededPlatforms.length === 1) {
              successMessage = `Posted successfully to ${succeededPlatforms[0]}`;
            } else if (succeededPlatforms.length === 2) {
              successMessage = `Posted successfully to ${succeededPlatforms[0]} and ${succeededPlatforms[1]}`;
            } else {
              const lastPlatform = succeededPlatforms.pop();
              successMessage = `Posted successfully to ${succeededPlatforms.join(', ')} and ${lastPlatform}`;
            }
          }
          
          // Build error messages for failed platforms
          const failedPlatforms = [];
          if (isFacebookEnabled && facebookFailed) {
            failedPlatforms.push(`Facebook: ${facebookPost?.error || 'Unknown error'}`);
          }
          if (isInstagramEnabled && instagramFailed) {
            failedPlatforms.push(`Instagram: ${instagramPost?.error || 'Unknown error'}`);
          }
          
          // Show warning if any platform failed but others succeeded
          if (failedPlatforms.length > 0 && succeededPlatforms.length > 0) {
            Swal.fire({
              position: 'center',
              icon: 'warning',
              title: successMessage,
              html: `<p style="margin-top: 10px; color: #666;">${failedPlatforms.join('<br/>')}</p>`,
              showConfirmButton: true,
              confirmButtonText: 'OK',
              confirmButtonColor: '#2e3192',
            });
          } else {
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: successMessage,
              showConfirmButton: false,
              timer: 3000,
            });
          }
        } else {
          // All platforms failed - show error
          const errorMessages = [];
          if (isFacebookEnabled && facebookFailed) {
            errorMessages.push(`Facebook: ${facebookPost?.error || 'Unknown error'}`);
          }
          if (isInstagramEnabled && instagramFailed) {
            errorMessages.push(`Instagram: ${instagramPost?.error || 'Unknown error'}`);
          }
          if (!linkedinPostId && !isFacebookEnabled && !isInstagramEnabled) {
            errorMessages.push('Failed to post to LinkedIn');
          }
          
          const errorMsg = errorMessages.length > 0 ? errorMessages.join('\n') : 'Failed to post';
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: 'Post Failed',
            html: `<p style="white-space: pre-line;">${errorMsg}</p>`,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#2e3192',
          });
          setIsPublishing(false);
          return;
        }

        // Reset form
        setPostContent('');
        setAiGeneratedContent('');
        setCustomPrompt('');
        setSelectedImages([]);
        setHashtags('');
        setImage(null);
        setSelectedImagename('');
        setSelectedImageUrl(null);
        setFetchedImages([]);
        setImagePreview(null);
        setVideoPreview(null);
        setPollData({ question: '', options: ['', '', '', ''] });
        setPostType('content');
        
        // Refresh feature usage data
        if (window.refreshFeatureUsage) {
          window.refreshFeatureUsage();
        }
        
      } else {
        const errorMessage = enabledPlatforms.length > 0 
          ? `Error posting to ${enabledPlatforms.map(p => p.name).join(' & ')}!`
          : 'Error posting!';
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: errorMessage,
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Error posting to LinkedIn:', error.response ? error.response.data : error.message);
      
      // Check for image validation errors
      const errorData = error.response?.data;
      if (errorData?.error && errorData?.errorType === 'image_validation') {
        const platformName = errorData.platform === 'instagram' ? 'Instagram' : errorData.platform;
        const errorDetails = errorData.errorDetails || {};
        const aspectRatio = errorDetails.aspectRatio || 'N/A';
        const dimensions = errorDetails.dimensions || 'N/A';
        
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Error`,
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 10px;"><strong>${errorData.message}</strong></p>
              ${dimensions !== 'N/A' ? `<p style="margin-bottom: 5px;">Image dimensions: ${dimensions}</p>` : ''}
              ${aspectRatio !== 'N/A' ? `<p style="margin-bottom: 5px;">Aspect ratio: ${aspectRatio}:1</p>` : ''}
              <p style="margin-top: 10px; color: #666;">Please upload a different image with a valid aspect ratio (between 0.8:1 and 1.91:1).</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          width: '500px'
        });
        setIsPublishing(false);
        return;
      }
      
      // Check for image required errors
      if (errorData?.error && errorData?.errorType === 'image_required') {
        const platformName = errorData.platform === 'instagram' ? 'Instagram' : errorData.platform;
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `${platformName} Image Required`,
          text: errorData.message || 'Image is required for this platform.',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
        setIsPublishing(false);
        return;
      }
      
      // Check if error is related to platform settings
      if (error.response?.status === 403 && (errorData?.errorType === 'module_disabled' || errorData?.errorType === 'all_platforms_disabled' || errorData?.errorType === 'platform_disabled')) {
        // Show modal with link to platform settings
        Swal.fire({
          icon: 'warning',
          title: 'Platform Settings Required',
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 15px;">${errorData.message || errorData.error || 'Please enable platform settings to post content.'}</p>
              <p style="margin-bottom: 20px; color: #666;">Go to Platform Settings to enable the Create Post module and select at least one platform.</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: 'Go to Platform Settings',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#2e3192',
          cancelButtonColor: '#6c757d',
        }).then((result) => {
          if (result.isConfirmed) {
            // Navigate to platform settings page
            navigate('/platformsettings');
          }
        });
      } else {
        // Regular error handling
        const errorMessage = enabledPlatforms.length > 0 
          ? `Failed to post to ${enabledPlatforms.map(p => p.name).join(' & ')}.`
          : 'Failed to post.';
        
        // Check if error response includes Instagram error details
        const instagramError = errorData?.instagramPost?.error;
        const errorText = instagramError 
          ? `Instagram: ${instagramError}${errorData?.error ? `\n\n${errorData.error}` : ''}`
          : (errorData?.error || error.message || 'An unexpected error occurred.');
        
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: errorMessage,
          text: errorText,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div style={{ 
      background: '#f3f2ef',
      minHeight: '100vh',
      padding: '0'
    }}>
      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <SideNavbar>
        <div className="card shadow-sm p-3 mt-3" style={{ border: 'none', backgroundColor: '#fff' }}>
        <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>
                {/* Header */}
                <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
                  Create a post
                </h5>
                <hr />
                <div className="create-post-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '24px',
                  paddingBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: '1 1 auto',
                    minWidth: '200px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: '#2e3192',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      <FontAwesomeIcon icon={faEdit} style={{ color: 'white', fontSize: '18px' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2 style={{ 
                        color: '#000000',
                        fontWeight: '600',
                        fontSize: '20px',
                        margin: '0 0 4px 0'
                      }}>
                        Create a post
                      </h2>
                      <p style={{ color: '#666666', margin: '0', fontSize: '14px' }}>
                        Share your thoughts with your network
                      </p>
                    </div>
                  </div>
                  
                  {/* Navigation Icons */}
                  <div className="create-post-nav-icons" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                      onClick={() => navigate('/schedule-list')}
                      title="View Schedule List"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        color: '#2e3192',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f3f2ef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faLineChart} style={{ fontSize: '20px' }} />
                      <span style={{ fontSize: '11px', fontWeight: '500' }}>Schedule</span>
                    </button>
                    
                    <button
                      onClick={() => navigate('/viewallpendingposts')}
                      title="View Pending Posts"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        color: '#2e3192',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f3f2ef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '20px' }} />
                      <span style={{ fontSize: '11px', fontWeight: '500' }}>Pending</span>
                    </button>
                    
                    <button
                      onClick={() => navigate('/viewallapprovedposts')}
                      title="View Approved Posts"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        color: '#2e3192',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f3f2ef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '20px' }} />
                      <span style={{ fontSize: '11px', fontWeight: '500' }}>Approved</span>
                    </button>
                  
                    <button
                      onClick={() => navigate('/viewallpostedposts')}
                      title="View Published Posts"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        color: '#2e3192',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f3f2ef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: '20px' }} />
                      <span style={{ fontSize: '11px', fontWeight: '500' }}>Published</span>
                    </button>
                  </div>
                </div>
                </div>
                
                <div className="card-body">
                  {/* Prompt Input Section */}
                  <div style={{ marginBottom: '20px' }}>
                    {/* <label style={{ fontSize: '14px', fontWeight: '500', color: '#2e3192', marginBottom: '8px', display: 'block' }}>Prompt:</label> */}
                    <textarea
                      placeholder="What do you want to talk about?"
                        value={customPrompt}
                        onChange={(e) => {
                          setCustomPrompt(e.target.value);
                          setSelectedPrompt('');
                        }}
                        style={{
                          width: '100%',
                        border: 'none',
                        fontSize: '16px',
                        padding: '16px 0',
                        background: 'transparent',
                        resize: 'none',
                          outline: 'none',
                        minHeight: '100px',
                        fontFamily: 'inherit'
                      }}
                    />
                    {/* Toolbar */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      paddingTop: '16px',
                      borderTop: '1px solid #e0dfdc'
                    }}>
                      {/* Left side: Lightbulb */}
                                             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <button
                           onClick={() => {
                             console.log('Button clicked, showPromptDropdown:', showPromptDropdown);
                             console.log('userPrompts:', userPrompts);
                             setShowPromptDropdown(!showPromptDropdown);
                           }}
                           title="Select Prompt Ideas for creating posts/polls"
                           style={{
                             background: 'transparent',
                             border: 'none',
                             cursor: 'pointer',
                             padding: '8px',
                             borderRadius: '4px',
                             color: '#666666',
                             transition: 'all 0.3s ease'
                           }}
                           onMouseEnter={(e) => {
                             e.target.style.backgroundColor = '#f3f2ef';
                           }}
                           onMouseLeave={(e) => {
                             e.target.style.backgroundColor = 'transparent';
                           }}
                         >
                           <FontAwesomeIcon icon={faLightbulb} style={{ fontSize: '20px' }} />
                         </button>
                       </div>
                      
                      {/* Right: Post and Poll buttons + Language Selector + Generate Content Button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        {/* Language Selection Dropdown */}
                        <div style={{ minWidth: '180px', flex: '0 0 auto' }}>
                          <Select
                            options={languageOptions}
                            value={selectedLanguage}
                            onChange={setSelectedLanguage}
                            placeholder="Select language"
                            isSearchable={true}
                            classNamePrefix="react-select"
                            styles={{
                              control: (base) => ({
                                ...base,
                                fontSize: '13px',
                                minHeight: '36px',
                                borderColor: '#cfeafb',
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            setPostType('content');
                            setPollData({ question: '', options: ['', '', '', ''] });
                          }}
                          title="Create Post"
                          style={{
                            background: postType === 'content' ? '#f0f4ff' : 'transparent',
                            border: postType === 'content' ? '2px solid #2e3192' : 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '8px',
                            color: postType === 'content' ? '#2e3192' : '#666666',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f3f2ef';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <FontAwesomeIcon icon={faFileText} style={{ fontSize: '20px' }} />
                        </button>
                        
                        <button
                          onClick={() => {
                            if (isFreeUser) return;
                            setPostType('poll');
                            setPostContent('');
                            setAiGeneratedContent('');
                          }}
                          title={isFreeUser ? "Polls are restricted for free users. Please upgrade/subscribe to use polls." : "Create Poll"}
                          disabled={isFreeUser}
                          style={{ 
                            background: postType === 'poll' ? '#f0f4ff' : 'transparent',
                            border: postType === 'poll' ? '2px solid #2e3192' : 'none',
                            cursor: 'pointer', 
                            padding: '8px',
                            borderRadius: '8px',
                            color: isFreeUser ? '#aaaaaa' : (postType === 'poll' ? '#2e3192' : '#666666'),
                            transition: 'all 0.3s ease',
                            opacity: isFreeUser ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!isFreeUser) {
                            e.target.style.backgroundColor = '#f3f2ef';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isFreeUser) {
                            e.target.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faPoll} style={{ fontSize: '20px' }} />
                        </button>
                        
                        {/* Spacer between icons and button */}
                        <div style={{ width: '16px' }}></div>
                        
                        {/* Generate Content Button */}
                        <button
                        onClick={generateContent}
                        disabled={(isGenerating || isGeneratingPoll) || !customPrompt.trim()}
                        style={{
                          background: (isGenerating || isGeneratingPoll) || !customPrompt.trim()
                            ? '#cccccc'
                            : '#2e3192',
                          border: 'none',
                          borderRadius: '20px',
                          padding: '8px 16px',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (isGenerating || isGeneratingPoll) || !customPrompt.trim() ? 'not-allowed' : 'pointer',
                          opacity: (isGenerating || isGeneratingPoll) || !customPrompt.trim() ? 0.7 : 1,
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          if (!(isGenerating || isGeneratingPoll) && customPrompt.trim()) {
                            e.target.style.backgroundColor = '#1a1d5c';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!(isGenerating || isGeneratingPoll) && customPrompt.trim()) {
                            e.target.style.backgroundColor = '#2e3192';
                          }
                        }}
                                                                                                                                                                                       >
                                                       {(isGenerating || isGeneratingPoll) ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} style={{ animation: 'spin 1s linear infinite' }} />
                                {postType === 'poll' ? 'Querying...' : 'Crafting...'}
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faStar} />
                                {postType === 'poll' ? 'Query' : 'Craft'}
                              </>
                        )}
                        </button>
                      </div>
                      
                  </div>

                  {/* Post Content Display */}
                  {postType === 'content' && (
                    <div className="mb-4">
                      <h5 style={{ color: '#2e3192', fontWeight: 'bold', marginBottom: '12px' }}>Smart Content</h5>
                      <textarea
                        value={displayContent}
                        onChange={(e) => {
                          const content = e.target.value;
                          if (content.length > 2950) {
                            Swal.fire({
                              icon: 'warning',
                              title: 'Content Length Exceeded',
                              text: 'The content should not exceed 2950 characters.',
                              showConfirmButton: true,
                            });
                          } else {
                            setAiGeneratedContent(content);
                          }
                        }}
                        style={{
                          fontSize: '15px',
                          borderRadius: '8px',
                          border: '1px solid #cfeafb',
                          background: '#FFFFFF',
                          height: displayContent ? '400px' : '100px',
                          width: '100%',
                          padding: '15px',
                          resize: 'vertical'
                        }}
                        placeholder="AI-generated post content appears here..."
                        rows="4"
                        cols="50"
                      />
                    </div>
                  )}

                  {/* Poll Display */}
                  {postType === 'poll' && (
                    <div className="mb-4">
                      <h5 style={{ color: '#2e3192', fontWeight: 'bold', marginBottom: '12px' }}>Smart Poll</h5>
                      {pollData.question ? (
                        <div className="card" style={{ border: '1px solid #cfeafb', borderRadius: '10px' }}>
                          <div className="card-body">
                            <div className="mb-3">
                              <label className="form-label" style={{ fontWeight: 'bold', color: '#2e3192' }}>
                                Poll Question:
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={pollData.question}
                                onChange={(e) => setPollData({ ...pollData, question: e.target.value })}
                                style={{ borderRadius: '8px', border: '1px solid #cfeafb' }}
                              />
                            </div>
                            
                            <div className="mb-3">
                              <label className="form-label" style={{ fontWeight: 'bold', color: '#2e3192' }}>
                                Poll Options:
                              </label>
                              {pollData.options.map((option, index) => (
                                <div key={index} className="mb-2">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={option}
                                    onChange={(e) => {
                                      const newOptions = [...pollData.options];
                                      newOptions[index] = e.target.value;
                                      setPollData({ ...pollData, options: newOptions });
                                    }}
                                    placeholder={`Option ${index + 1}`}
                                    style={{ borderRadius: '8px', border: '1px solid #cfeafb' }}
                                  />
                                </div>
                              ))}
                            </div>
                            
                          </div>
                        </div>
                      ) : (
                        <div className="text-center p-4" style={{ border: '2px dashed #cfeafb', borderRadius: '10px' }}>
                          <FontAwesomeIcon icon={faPoll} style={{ fontSize: '3rem', color: '#cfeafb', marginBottom: '10px' }} />
                          <p style={{ color: '#6c757d' }}>Generate a poll to see it here</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Media Suggestions - Only for Post content and when content is generated */}
                  {postType === 'content' && aiGeneratedContent?.trim() && (
                    <div className="mb-4">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h5 style={{ 
                          color: '#1a202c', 
                          fontWeight: '600', 
                          marginBottom: '0',
                          fontSize: '1.1rem'
                        }}>
                          AI Media Suggestions
                        </h5>
                        <button
                          onClick={() => {
                            setImageKeywords(''); // Clear manual keywords
                            fetchImagesWithKeywords(true); // Use auto-extraction, pass true for isRefresh
                          }}
                          disabled={isRefreshingImages || isCraftingVisual || !aiGeneratedContent?.trim()}
                          style={{
                            background: (isRefreshingImages || isCraftingVisual || !aiGeneratedContent?.trim()) ? '#cccccc' : '#2e3192',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            color: 'white',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: (isRefreshingImages || isCraftingVisual || !aiGeneratedContent?.trim()) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isRefreshingImages && !isCraftingVisual && aiGeneratedContent?.trim()) {
                              e.target.style.backgroundColor = '#1a1d5c';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isRefreshingImages && !isCraftingVisual && aiGeneratedContent?.trim()) {
                              e.target.style.backgroundColor = '#2e3192';
                            }
                          }}
                          title="Refresh images using auto-extracted keywords from your prompt and content"
                        >
                          <FontAwesomeIcon 
                            icon={isRefreshingImages ? faSpinner : faImages} 
                            style={{ 
                              fontSize: '12px',
                              animation: isRefreshingImages ? 'spin 1s linear infinite' : 'none'
                            }} 
                          />
                          {isRefreshingImages ? 'Searching...' : 'Refresh'}
                        </button>
                      </div>
                      
                      {/* Image Keywords Input */}
                      {aiGeneratedContent?.trim() && (
                        <div className="mb-3">
                          <label style={{ 
                            fontSize: '13px', 
                            fontWeight: '500', 
                            color: '#2e3192',
                            marginBottom: '6px',
                            display: 'block'
                          }}>
                            Image Keywords (Optional - comma-separated)
                          </label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={imageKeywords}
                              onChange={(e) => setImageKeywords(e.target.value)}
                              placeholder="e.g., business, technology, AI, innovation"
                              style={{
                                flex: 1,
                                fontSize: '14px',
                                borderRadius: '6px',
                                border: '1px solid #cfeafb',
                                background: '#FFFFFF',
                                padding: '8px 12px',
                                outline: 'none'
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && imageKeywords.trim()) {
                                  fetchImagesWithKeywords(false);
                                }
                              }}
                            />
                            <button
                              onClick={() => fetchImagesWithKeywords(false)}
                              disabled={isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()}
                              style={{
                                background: (isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()) ? '#cccccc' : '#2e3192',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                color: 'white',
                                fontSize: '13px',
                                fontWeight: '500',
                                cursor: (isCraftingVisual || isRefreshingImages || !imageKeywords?.trim()) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                if (!isCraftingVisual && !isRefreshingImages && imageKeywords?.trim()) {
                                  e.target.style.backgroundColor = '#1a1d5c';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isCraftingVisual && !isRefreshingImages && imageKeywords?.trim()) {
                                  e.target.style.backgroundColor = '#2e3192';
                                }
                              }}
                              title="Generate images using your keywords"
                            >
                              <FontAwesomeIcon 
                                icon={isCraftingVisual ? faSpinner : faImage} 
                                style={{ 
                                  fontSize: '12px',
                                  animation: isCraftingVisual ? 'spin 1s linear infinite' : 'none'
                                }} 
                              />
                              {isCraftingVisual ? 'Crafting...' : 'Craft Visual'}
                            </button>
                          </div>
                          <p style={{ 
                            fontSize: '11px', 
                            color: '#6c757d', 
                            marginTop: '4px',
                            marginBottom: '0'
                          }}>
                            Enter keywords and click "Craft Visual" to generate images, or leave empty and use "Refresh" for auto-extraction
                          </p>
                        </div>
                      )}
                      
                        {(isFetchingImages || isRefreshingImages || isCraftingVisual) ? (
                          <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted small">Fetching images...</p>
                          </div>
                        ) : fetchedImages.length > 0 ? (
                        <div className="row" style={{ gap: '4px' }}>
                          {fetchedImages.map((img, index) => (
                            <div key={index} className="col-4 col-md-3 col-lg-2 col-xl-2">
                              <div 
                                className="position-relative"
                                style={{ 
                                  aspectRatio: "1",
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  border: selectedImageUrl === img ? "2px solid #2e3192" : "1px solid transparent",
                                  transition: "all 0.2s ease",
                                  backgroundColor: "#f7fafc",
                                  maxWidth: "120px"
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedImageUrl !== img) {
                                    e.currentTarget.style.borderColor = "#cbd5e0";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedImageUrl !== img) {
                                    e.currentTarget.style.borderColor = "transparent";
                                  }
                                }}
                                onClick={() => handleSelectSuggestedImage(img)}
                              >
                                <img
                                  src={img}
                                  alt="Suggested"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block"
                                  }}
                                />
                                
                                {/* Top-right checkbox */}
                                <div
                                  className="position-absolute"
                                  style={{ 
                                    top: "6px", 
                                    right: "6px",
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "50%",
                                    backgroundColor: selectedImagename === img ? "#2e3192" : "rgba(255, 255, 255, 0.95)",
                                    border: selectedImagename === img ? "none" : "2px solid rgba(0, 0, 0, 0.2)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)"
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newChecked = !(selectedImagename === img);
                                    setSelectedImagename(newChecked ? img : null);
                                    setSelectedImageUrl(newChecked ? img : null);
                                    if (newChecked) {
                                      setImage(null);
                                      setImagePreview(null);
                                      setVideoPreview(null);
                                    }
                                  }}
                                >
                                  {selectedImagename === img && (
                                    <FontAwesomeIcon 
                                      icon={faCheck} 
                                      style={{ 
                                        color: "#fff", 
                                        fontSize: "10px"
                                      }} 
                                    />
                                  )}
                              </div>
                                
                                {/* Bottom action buttons overlay */}
                                <div
                                  className="position-absolute d-flex gap-2"
                                  style={{
                                    bottom: "8px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    opacity: selectedImageUrl === img ? "1" : "0",
                                    transition: "opacity 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.opacity = "1";
                                  }}
                                  onMouseLeave={(e) => {
                                    if (selectedImageUrl !== img) {
                                      e.currentTarget.style.opacity = "0";
                                    }
                                  }}
                                >
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      minWidth: "32px",
                                      height: "32px",
                                      padding: "0",
                                      borderRadius: "6px",
                                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                                      border: "none",
                                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImageUrl(img);
                                      setIsModalOpen(true);
                                    }}
                                    title="View Full Image"
                                  >
                                    <FontAwesomeIcon icon={faImage} style={{ color: "#2e3192", fontSize: "12px" }} />
                                  </button>
                                  
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      minWidth: "32px",
                                      height: "32px",
                                      padding: "0",
                                      borderRadius: "6px",
                                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                                      border: "none",
                                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                      display: "none",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}
                                    onClick={(e) => handleDownloadImage(img, e)}
                                    title="Download Image"
                                  >
                                    <FontAwesomeIcon icon={faDownload} style={{ color: "#2e3192", fontSize: "12px" }} />
                                  </button>
                                  
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      minWidth: "32px",
                                      height: "32px",
                                      padding: "0",
                                      borderRadius: "6px",
                                      backgroundColor: "#2e3192",
                                      border: "none",
                                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                                      display: "none",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImagename(img);
                                      setSelectedImageUrl(img);
                                      setImage(null);
                                      setImagePreview(null);
                                      setVideoPreview(null);
                                      setFileInputKey(prev => prev + 1);
                                    }}
                                    title="Select This Image"
                                  >
                                    <FontAwesomeIcon icon={faCheck} style={{ color: "#fff", fontSize: "12px" }} />
                                  </button>
                                </div>
                                
                                {/* Hover overlay */}
                                {selectedImageUrl !== img && (
                                  <div
                                    className="position-absolute"
                                    style={{
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: "rgba(0, 0, 0, 0)",
                                      transition: "background-color 0.2s ease",
                                      pointerEvents: "none"
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0)";
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : aiGeneratedContent ? (
                        <div className="text-center p-4" style={{ 
                          border: '1px dashed #dee2e6', 
                          borderRadius: '8px', 
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d'
                        }}>
                          <FontAwesomeIcon icon={faImage} style={{ fontSize: '2rem', color: '#adb5bd', marginBottom: '10px' }} />
                          <p className="mb-0 small">
                            No images found for this content. Try adding hashtags to your prompt.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {/* Upload Image/Video Section - Only for Post content */}
                  {postType === 'content' && (
                    <div className="mb-4">
                      <div className="form-group">
                         <input
                           key={fileInputKey}
                           ref={fileInputRef}
                           type="file"
                           onChange={(e) => {
                             handleImageChange(e);
                             setSelectedImageUrl(null); // Clear suggested image selection when a file is chosen
                           }}
                           className="form-control"
                           accept="image/*,video/*"
                           style={{
                             fontSize: '15px',
                             borderRadius: '8px',
                             border: '1px solid #cfeafb',
                             background: '#FFFFFF',
                             padding: '10px',
                             display: 'none' // Hide the file input completely
                           }}
                         />
                         {/* Custom upload button - Circular style */}
                         <button
                           type="button"
                           onClick={() => fileInputRef.current?.click()}
                           title="Upload Image or Video"
                           style={{
                             fontSize: '16px',
                             borderRadius: '50%',
                             border: 'none',
                             background: '#2e3192',
                             padding: '12px',
                             color: 'white',
                             fontWeight: 'bold',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             width: '48px',
                             height: '48px',
                             minWidth: '48px',
                             transition: 'all 0.3s ease',
                             cursor: 'pointer'
                           }}
                           onMouseEnter={(e) => {
                             e.target.style.backgroundColor = '#1a1d5c';
                             e.target.style.transform = 'scale(1.05)';
                           }}
                           onMouseLeave={(e) => {
                             e.target.style.backgroundColor = '#2e3192';
                             e.target.style.transform = 'scale(1)';
                           }}
                         >
                           <FontAwesomeIcon icon={faUpload} />
                         </button>
                        {(image || selectedImageUrl) && (
                          <div className="mt-2">
                            {image ? (
                              // Show uploaded file preview (image or video)
                              <div>
                                <p className="mb-0" style={{ color: '#2e3192', fontWeight: 'bold' }}>
                                  Selected: {(image.type && image.type.startsWith('video/')) ? 'Uploaded Video' : 'Uploaded Image'}
                                </p>
                                {imagePreview && (
                                  <div className="mt-2">
                                    <img
                                      src={imagePreview}
                                      alt="Uploaded"
                                      style={{
                                        width: "100px",
                                        height: "100px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        border: "2px solid #2e3192"
                                      }}
                                    />
                                  </div>
                                )}
                                {videoPreview && (
                                  <div className="mt-2">
                                    <video
                                      src={videoPreview}
                                      controls
                                      style={{
                                        width: "200px",
                                        maxHeight: "200px",
                                        borderRadius: "8px",
                                        border: "2px solid #2e3192"
                                      }}
                                    >
                                      Your browser does not support the video tag.
                                    </video>
                                  </div>
                                )}
                              </div>
                            ) : selectedImageUrl ? (
                              // Show selected AI image preview only
                              <div>
                                <p className="mb-0" style={{ color: '#2e3192', fontWeight: 'bold' }}>
                                  Selected: AI Suggested Image
                                </p>
                                <div className="mt-2">
                                  <img
                                    src={selectedImageUrl}
                                    alt="Selected"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "2px solid #2e3192"
                                    }}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Schedule Options */}
                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="scheduleToggle"
                          checked={scheduleForLater}
                          onChange={(e) => setScheduleForLater(e.target.checked)}
                          style={{ 
                            transform: 'scale(1.2)',
                            cursor: 'pointer'
                          }}
                        />
                        <label 
                          className="form-check-label" 
                          htmlFor="scheduleToggle"
                          style={{ 
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            color: scheduleForLater ? '#2e3192' : '#6c757d'
                          }}
                        >
                          Schedule for Later
                        </label>
                      </div>
                      
                    </div>
                    
                     {scheduleForLater && (
                       <div className="mt-3">
                         <div className="row">
                           <div className="col-md-6">
                             <label className="form-label">Schedule Date & Time</label>
                             <input
                               type="datetime-local"
                               className="form-control"
                               value={scheduledDate}
                               onChange={(e) => setScheduledDate(e.target.value)}
                               style={{ borderRadius: '8px', border: '1px solid #cfeafb' }}
                             />
                           </div>
                           <div className="col-md-6">
                          <label className="form-label">Timezone</label>
                          <div style={{ borderRadius: '8px', border: '1px solid #cfeafb' }}>
                            <TimezoneSelect
                              value={timezone}
                              onChange={setTimezone}
                              placeholder="Select timezone"
                            />
                          </div>
                           </div>
                         </div>
                         <div className="mt-2">
                             <small className="text-muted">
                             Scheduled for: {scheduledDate && timezone ? (() => {
                               // Handle both string and object formats for timezone
                               const tzValue = typeof timezone === 'string' ? timezone : timezone?.value;
                               if (!tzValue) return null;
                               
                               // Parse the datetime-local string directly (format: "YYYY-MM-DDTHH:mm")
                               // The datetime-local input gives us the exact values the user entered
                               // We'll display them exactly as entered, just formatted nicely
                               const dateStr = scheduledDate; // e.g., "2025-11-04T12:16"
                               const [datePart, timePart] = dateStr.split('T');
                               const [year, month, day] = datePart.split('-').map(Number);
                               const [hour, minute] = (timePart || '').split(':').map(Number);
                               
                               // Format the date components directly (no timezone conversion)
                               const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                               const monthName = monthNames[month - 1];
                               
                               // Convert 24-hour to 12-hour format
                               let hour12 = hour;
                               let ampm = 'AM';
                               if (hour === 0) {
                                 hour12 = 12;
                               } else if (hour === 12) {
                                 hour12 = 12;
                                 ampm = 'PM';
                               } else if (hour > 12) {
                                 hour12 = hour - 12;
                                 ampm = 'PM';
                               }
                               
                               const minuteStr = String(minute).padStart(2, '0');
                               
                               // Get timezone abbreviation from the selected timezone
                               const tzAbbr = new Intl.DateTimeFormat('en-US', {
                                 timeZone: tzValue,
                                 timeZoneName: 'short'
                               }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || 'CST';
                               
                               return `${monthName} ${day}, ${year} at ${hour12}:${minuteStr} ${ampm} ${tzAbbr}`;
                             })() : scheduledDate ? (() => {
                               // Fallback: just show the date as entered
                               const date = new Date(scheduledDate);
                               return date.toLocaleString('en-US', { 
                                 year: 'numeric',
                                 month: 'long',
                                 day: 'numeric',
                                 hour: '2-digit',
                                 minute: '2-digit'
                               });
                             })() : 'Please select a date and time'}
                           </small>
                         </div>
                       </div>
                     )}

                    {/* Platform Selection - Only show for Page accounts */}
                    {!platformsLoading && enabledPlatforms.length > 0 && (localStorage.getItem('AccountType') !== 'User' && localStorage.getItem('AccountType') !== 'user') && (
                      <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0dfdc'
                      }}>
                        <label style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2e3192',
                          marginBottom: '12px',
                          display: 'block'
                        }}>
                          Select Platforms to Post To:
                        </label>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '16px',
                          alignItems: 'center'
                        }}>
                          {enabledPlatforms.map((platform) => {
                            const platformIcons = {
                              linkedin: <FaLinkedin style={{ color: '#0077b5', fontSize: '24px' }} />,
                              facebook: <FaFacebook style={{ color: '#1877F2', fontSize: '24px' }} />,
                              instagram: <FaInstagram style={{ color: '#E4405F', fontSize: '24px' }} />
                            };
                            const isSelected = selectedPlatforms.includes(platform.code);
                            
                            return (
                              <label
                                key={platform.code}
                                title={platform.name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  padding: '10px',
                                  borderRadius: '8px',
                                  backgroundColor: isSelected ? '#e8f0fe' : 'white',
                                  border: `2px solid ${isSelected ? '#2e3192' : '#e0dfdc'}`,
                                  transition: 'all 0.2s ease',
                                  userSelect: 'none',
                                  minWidth: '48px',
                                  height: '48px',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = isSelected ? '#d0e7ff' : '#f0f0f0';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = isSelected ? '#e8f0fe' : 'white';
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPlatforms([...selectedPlatforms, platform.code]);
                                    } else {
                                      // Prevent unchecking if it's the last selected platform
                                      if (selectedPlatforms.length > 1) {
                                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform.code));
                                      } else {
                                        Swal.fire({
                                          icon: 'warning',
                                          title: 'At Least One Platform Required',
                                          text: 'Please select at least one platform to post to.',
                                          confirmButtonText: 'OK',
                                          confirmButtonColor: '#2e3192',
                                          timer: 2000
                                        });
                                      }
                                    }
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#2e3192',
                                    zIndex: 1
                                  }}
                                />
                                <span style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {platformIcons[platform.code] || <FaLinkedin style={{ fontSize: '24px' }} />}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <small style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: '8px',
                          color: '#666',
                          fontSize: '12px',
                          gap: '8px'
                        }}>
                          Selected: {selectedPlatforms.length > 0 
                            ? selectedPlatforms.map((code, index) => {
                                const platformIcons = {
                                  linkedin: <FaLinkedin key={index} style={{ color: '#0077b5', fontSize: '16px' }} />,
                                  facebook: <FaFacebook key={index} style={{ color: '#1877F2', fontSize: '16px' }} />,
                                  instagram: <FaInstagram key={index} style={{ color: '#E4405F', fontSize: '16px' }} />
                                };
                                return platformIcons[code] || null;
                              })
                            : 'None'}
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '16px',
                    borderTop: '1px solid #e0dfdc'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={handlePublish}
                      disabled={(!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isPublishing || scheduleForLater || (!platformsLoading && (enabledPlatforms.length === 0 || selectedPlatforms.length === 0))}
                      style={{
                          background: (!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isPublishing || scheduleForLater || (!platformsLoading && enabledPlatforms.length === 0)
                            ? '#cccccc'
                            : '#2e3192',
                        border: 'none',
                          borderRadius: '20px',
                          padding: '8px 24px',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isPublishing || scheduleForLater || (!platformsLoading && enabledPlatforms.length === 0) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!((!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isPublishing || scheduleForLater || (!platformsLoading && enabledPlatforms.length === 0))) {
                            e.target.style.backgroundColor = '#1a1d5c';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!((!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isPublishing || scheduleForLater || (!platformsLoading && enabledPlatforms.length === 0))) {
                            e.target.style.backgroundColor = '#2e3192';
                          }
                        }}
                      >
                        {isPublishing 
                          ? (platformsLoading ? 'Loading...' : `Publishing${selectedPlatforms.length > 0 ? ` to ${selectedPlatforms.map(code => {
                              const platform = enabledPlatforms.find(p => p.code === code);
                              return platform ? platform.name : code;
                            }).join(' & ')}` : '...'}`)
                          : (platformsLoading ? 'Loading...' : selectedPlatforms.length > 0 
                            ? formatPlatformNamesForButton(selectedPlatforms.map(code => {
                                const platform = enabledPlatforms.find(p => p.code === code);
                                return platform || { name: code, code };
                              }))
                            : 'Select Platform')
                        }
                    </button>
                    
                     <button
                       onClick={handleSchedulePost}
                       disabled={(!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isScheduling || !scheduleForLater || !scheduledDate || (!platformsLoading && (enabledPlatforms.length === 0 || selectedPlatforms.length === 0))}
                       style={{
                          background: (!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isScheduling || !scheduleForLater || !scheduledDate || (!platformsLoading && enabledPlatforms.length === 0)
                            ? '#cccccc'
                            : '#2e3192',
                          border: 'none',
                          borderRadius: '20px',
                          padding: '8px 24px',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isScheduling || !scheduleForLater || !scheduledDate || (!platformsLoading && enabledPlatforms.length === 0) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!((!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isScheduling || !scheduleForLater || !scheduledDate || (!platformsLoading && enabledPlatforms.length === 0))) {
                            e.target.style.backgroundColor = '#1a1d5c';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!((!postContent.trim() && !aiGeneratedContent.trim() && !(postType === 'poll' && pollData.question && pollData.options.some(opt => opt.trim()))) || isScheduling || !scheduleForLater || !scheduledDate || (!platformsLoading && enabledPlatforms.length === 0))) {
                            e.target.style.backgroundColor = '#2e3192';
                          }
                        }}
                      >
                        {isScheduling 
                          ? (platformsLoading ? 'Loading...' : `Scheduling${selectedPlatforms.length > 0 ? ` to ${selectedPlatforms.map(code => {
                              const platform = enabledPlatforms.find(p => p.code === code);
                              return platform ? platform.name : code;
                            }).join(' & ')}` : '...'}`)
                          : (platformsLoading ? 'Loading...' : selectedPlatforms.length > 0 
                            ? formatPlatformNamesForButton(selectedPlatforms.map(code => {
                                const platform = enabledPlatforms.find(p => p.code === code);
                                return platform || { name: code, code };
                              })).replace('Post to ', 'Schedule to ')
                            : 'Select Platform')
                        }
                     </button>
                    </div>
                  </div>
                </div>
                
        {/* Modal for Image Preview */}
        <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} size={isMaximized ? "xl" : "lg"} centered>
          <Modal.Header closeButton>
            <Modal.Title>Image Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <img
              src={selectedImageUrl}
              alt="Selected"
              style={{
                maxWidth: "100%",
                maxHeight: isMaximized ? "90vh" : "70vh",
                objectFit: "contain",
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => setIsMaximized(!isMaximized)}>
              {isMaximized ? "Minimize" : "Maximize"}
            </Button>
            <Button variant="success" onClick={() => {
              handleSelectSuggestedImage(selectedImageUrl);
              setIsModalOpen(false);
            }}>
              Select Image
            </Button>
          </Modal.Footer>
        </Modal>

                 {/* Add Post Prompt Modal */}
                 {showAddPromptModal && (
                    <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add Post Prompt</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowAddPromptModal(false)} />
                                </div>
                                <div className="modal-body">
                                    <textarea
                                        value={newPromptText}
                                        onChange={(e) => setNewPromptText(e.target.value)}
                                        className="form-control"
                                        placeholder="Enter your prompt"
                                        rows={4}
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => {
                                        setShowAddPromptModal(false);
                                        setNewPromptText('');
                                    }}>Cancel</button>
                                    <button type="button" className="btn btn-primary" onClick={handleSavePrompt} disabled={savingPrompt}>
                                        {savingPrompt ? 'Saving...' : 'Save Prompt'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                 {/* Saved Prompts Modal */}
         <Modal show={showPromptDropdown} onHide={() => setShowPromptDropdown(false)} size="md" centered>
           <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <Modal.Title style={{ margin: 0 }}>
               <FontAwesomeIcon icon={faLightbulb} style={{ color: '#2e3192', marginRight: '8px' }} />
               Prompts ({userPrompts.length})
             </Modal.Title>
             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <button
                 type="button"
                 title="Add prompt"
                 className="btn btn-link"
                 style={{
                   padding: '4px 8px',
                   textDecoration: 'none',
                   fontSize: 24,
                   fontWeight: 'bold',
                   color: '#000',
                   cursor: 'pointer',
                   background: '#f0f0f0',
                   border: '1px solid #ddd',
                   borderRadius: '4px',
                   lineHeight: 1,
                   minWidth: '32px',
                   height: '32px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.background = '#e0e0e0';
                   e.target.style.borderColor = '#2e3192';
                   e.target.style.color = '#2e3192';
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.background = '#f0f0f0';
                   e.target.style.borderColor = '#ddd';
                   e.target.style.color = '#000';
                 }}
                 onClick={(e) => {
                   e.stopPropagation();
                   setShowPromptDropdown(false);
                   setShowAddPromptModal(true);
                 }}
               >
                 +
               </button>
               <button
                 type="button"
                 className="btn-close"
                 onClick={() => setShowPromptDropdown(false)}
                 aria-label="Close"
               />
             </div>
           </Modal.Header>
          <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
            {userPrompts.length > 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {userPrompts.map((prompt, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      border: '1px solid #e0dfdc',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)'
                    }}
                    onClick={() => {
                      setCustomPrompt(prompt.PromptData);
                      setSelectedPrompt(prompt.PromptID);
                      setShowPromptDropdown(false);
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderColor = '#2e3192';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 115, 177, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.borderColor = '#e0dfdc';
                      e.target.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.04)';
                    }}
                  >
                    <div style={{ 
                      fontWeight: '600', 
                      color: '#000000', 
                      marginBottom: '4px',
                      fontSize: '13px',
                      lineHeight: '1.3'
                    }}>
                      {prompt.promptName}
                    </div>
                    <div style={{ 
                      color: '#666666', 
                      fontSize: '11px',
                      lineHeight: '1.4'
                    }}>
                      {prompt.PromptData.length > 80 ? prompt.PromptData.substring(0, 80) + '...' : prompt.PromptData}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#666666',
                fontSize: '14px'
              }}>
                <FontAwesomeIcon icon={faLightbulb} style={{ fontSize: '24px', marginBottom: '8px', opacity: 0.5 }} />
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>No saved prompts found</div>
                <div style={{ fontSize: '12px', opacity: 0.7 }}>
                  Create your first prompt to get started
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPromptDropdown(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
                 </div>
               </div>
       </SideNavbar>
     </div>
   );
 };

 export default CreatePostPage;
